import { useState, Fragment, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import DownloadIcon from '@mui/icons-material/Download';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Collapse from '@mui/material/Collapse';
import { useOnline } from '../../context/OnlineContext';
import analyticsService from '../../services/analytics';
import { cacheRoute, loadGPXFile } from '../charts/ChartsSlice';
import { removeFromCache } from './RouteSelectionSlice';

function RouteSelection({ setIsLoading, isLoading = false }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [expandedRowId, setExpandedRowId] = useState(null);
    const filteredRoutes = useSelector(state => state.routeSelection.filteredRoutes);
    const selectedRouteId = useSelector(state => state.charts.route_json.id);
    const chartSettings = useSelector(state => state.chartSettings.chartSettings);
    const gpxError = useSelector(state => state.charts.gpxError);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    // Skeleton data for loading state
    const skeletonRoutes = [
        { id: 'skeleton-1', name: 'Loading route...', distance: 0, elevation_gain: 0, isCached: false, isPublicRoute: false },
        { id: 'skeleton-2', name: 'Loading route...', distance: 0, elevation_gain: 0, isCached: false, isPublicRoute: false },
        { id: 'skeleton-3', name: 'Loading route...', distance: 0, elevation_gain: 0, isCached: false, isPublicRoute: false },
        { id: 'skeleton-4', name: 'Loading route...', distance: 0, elevation_gain: 0, isCached: false, isPublicRoute: false }
    ];

    const handleRouteSelect = (routeId) => {
        setIsLoading(true);
        const selectedRoute = filteredRoutes.find(route => String(route.id) === String(routeId));
        if (selectedRoute) {
            setIsLoading(false);
            analyticsService.trackRouteSelection(routeId, selectedRoute.name);
            const source = selectedRoute?.source || 'rwgps';
            navigate(`/cues/${source}/${routeId}`);
        }
    };

    const handleUpdateRoute = (routeId) => {
        const selectedRoute = filteredRoutes.find(route => String(route.id) === String(routeId));
        if (selectedRoute?.source === 'gpx') {
            // For GPX files, prompt for new file
            fileInputRef.current?.click();
        } else {
            // For other sources, cache/update the route
            const source = selectedRoute?.source || 'rwgps';
            dispatch(cacheRoute({ routeId, source }));
            analyticsService.trackUserAction('route_cached', { route_id: routeId });
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                const reader = new FileReader();
                reader.onload = async (event) => {
                    const gpxData = event.target.result;
                    await dispatch(loadGPXFile({ gpxData, fileName: file.name })).unwrap();
                };
                reader.readAsText(file);
            } catch (error) {
                console.error('Error loading GPX file:', error);
            } finally {
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        }
    };

    const handleRemoveFromCache = (routeId) => {
        dispatch(removeFromCache(routeId));
        analyticsService.trackUserAction('route_uncached', { route_id: routeId });
    };

    return (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            width: '100%'
        }}>
            <input
                type="file"
                accept=".gpx"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
            <TableContainer sx={{
                flex: 1,
                overflow: 'auto',
                width: '100%',
                '&::-webkit-scrollbar': {
                    width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: '#888',
                    borderRadius: '4px',
                },
            }}>
                <Table
                    size="small"
                    aria-label="simple table"
                    sx={{
                        width: '100%',
                        tableLayout: 'fixed',
                        '& td, & th': {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }
                    }}
                >
                    {!isMobile && (
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '48px', minWidth: '48px' }} />
                                <TableCell sx={{ minWidth: 0 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                        Route Name
                                    </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ width: '120px', minWidth: '120px' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                        Distance
                                    </Typography>
                                </TableCell>
                                <TableCell align="left" sx={{ width: '150px', minWidth: '150px' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                        Elevation
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ width: '150px', minWidth: '150px' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                        Action
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                    )}
                    <TableBody>
                        {(isLoading ? skeletonRoutes : filteredRoutes).map((row) => (
                            <RouteRow
                                key={row.id}
                                row={row}
                                chartSettings={chartSettings}
                                onRouteSelect={handleRouteSelect}
                                onUpdateRoute={handleUpdateRoute}
                                onRemoveFromCache={handleRemoveFromCache}
                                selectedRouteId={selectedRouteId}
                                expanded={expandedRowId === row.id}
                                onExpand={(e) => {
                                    e.stopPropagation();
                                    setExpandedRowId(expandedRowId === row.id ? null : row.id);
                                }}
                                isLoading={isLoading}
                                isMobile={isMobile}
                                fileInputRef={fileInputRef}
                                onFileChange={handleFileChange}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Snackbar
                open={!!gpxError}
                autoHideDuration={6000}
            >
                <Alert severity="error" sx={{ width: '100%' }}>
                    {gpxError}
                </Alert>
            </Snackbar>
        </Box>
    );
}

function RouteRow({ row, chartSettings, onRouteSelect, selectedRouteId, expanded, onExpand, onUpdateRoute, onRemoveFromCache, isLoading = false, isMobile = false, fileInputRef, onFileChange }) {
    const { isOnline } = useOnline();

    return (
        <Fragment key={row.name}>
            <TableRow
                sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    cursor: isLoading ? 'default' : 'pointer',
                    '&:hover': {
                        backgroundColor: isLoading ? 'transparent' : 'rgba(0, 0, 0, 0.04)'
                    },
                    opacity: isLoading ? 0.6 : 1,
                    filter: isLoading ? 'blur(1px)' : 'none'
                }}
                selected={row.id === selectedRouteId}
                onClick={isLoading ? undefined : (e) => {
                    e.stopPropagation();
                    onExpand(e);
                }}
            >
                {isMobile ? (
                    <>
                        {/* Mobile: Combined column */}
                        <TableCell
                            sx={{
                                p: 0.5,
                                minWidth: 0,
                                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                                width: '50%'
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                minWidth: 0
                            }}>
                                <IconButton
                                    aria-label="expand row"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onExpand(e);
                                    }}
                                    sx={{ flexShrink: 0 }}
                                >
                                    {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    minWidth: 0,
                                    flex: 1
                                }}>
                                    <Typography
                                        component="div"
                                        noWrap
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: '1.1rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            minWidth: 0
                                        }}
                                    >
                                        <Box sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flex: 1
                                        }}>
                                            {row.name}
                                        </Box>
                                        {row.source === 'gpx' && <GPXFileBadge />}
                                        {row.isPublicRoute && <PublicRouteBadge />}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            fontSize: '0.75rem'
                                        }}
                                    >
                                        {(row.distance * chartSettings.distMultiplier).toFixed(0)} {chartSettings.distAbbr} • {(row.elevation_gain * chartSettings.elevMultiplier).toFixed(0)} {chartSettings.elevAbbr} gain
                                    </Typography>
                                </Box>
                            </Box>
                        </TableCell>
                        {/* Mobile: Actions column */}
                        <TableCell
                            sx={{
                                p: 0.5,
                                minWidth: 0,
                                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                                width: '50%'
                            }}
                        >
                            <ActionButtons
                                row={row}
                                onRouteSelect={onRouteSelect}
                                isLoading={isLoading}
                                isMobile={isMobile}
                            />
                        </TableCell>
                    </>
                ) : (
                    <>
                        {/* Desktop: Toggle column */}
                        <TableCell sx={{
                            width: '48px',
                            minWidth: '48px',
                            p: 1,
                            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
                        }}>
                            <IconButton
                                aria-label="expand row"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onExpand(e);
                                }}
                            >
                                {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                        </TableCell>

                        {/* Desktop: Route Name column */}
                        <TableCell
                            sx={{
                                p: 1,
                                minWidth: 0,
                                borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                minWidth: 0
                            }}>
                                <Box sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1
                                }}>
                                    {row.name}
                                </Box>
                                {row.source === 'gpx' && <GPXFileBadge />}
                                {row.isPublicRoute && <PublicRouteBadge />}
                            </Box>
                        </TableCell>

                        {/* Desktop: Distance column */}
                        <TableCell
                            align="right"
                            sx={{
                                p: 1,
                                width: '120px',
                                minWidth: '120px',
                                borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
                            }}
                        >
                            {(row.distance * chartSettings.distMultiplier).toFixed(0)} {chartSettings.distAbbr}
                        </TableCell>

                        {/* Desktop: Elevation column */}
                        <TableCell
                            align="left"
                            sx={{
                                p: 1,
                                width: '150px',
                                minWidth: '150px',
                                borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <NorthEastIcon fontSize="small" />
                                {Math.round(row.elevation_gain * chartSettings.elevMultiplier)}{chartSettings.elevAbbr}
                            </Box>
                        </TableCell>

                        {/* Desktop: Action column */}
                        <TableCell
                            sx={{
                                p: 1,
                                width: '150px',
                                minWidth: '150px',
                                borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
                            }}
                        >
                            <ActionButtons
                                row={row}
                                onRouteSelect={onRouteSelect}
                                isLoading={isLoading}
                                isMobile={isMobile}
                            />
                        </TableCell>
                    </>
                )}
            </TableRow>
            <TableRow>
                <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={isMobile ? 2 : 5}
                    sx={{
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                        p: 0
                    }}
                >
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <ExpandedRowDetails
                            row={row}
                            chartSettings={chartSettings}
                            onRouteSelect={onRouteSelect}
                            onUpdateRoute={onUpdateRoute}
                            onRemoveFromCache={onRemoveFromCache}
                            isOnline={isOnline}
                            isMobile={isMobile}
                            fileInputRef={fileInputRef}
                            onFileChange={onFileChange}
                        />
                    </Collapse>
                </TableCell>
            </TableRow>
        </Fragment>
    );
}

function PublicRouteBadge() {
    return (
        <Tooltip title="Public Route">
            <Box
                sx={{
                    px: 0.5,
                    py: 0.25,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    color: 'primary.main',
                    backgroundColor: 'primary.50',
                    borderRadius: '4px',
                    border: '1px solid',
                    borderColor: 'primary.200',
                    flexShrink: 0
                }}
            >
                PUBLIC
            </Box>
        </Tooltip>
    );
}

function GPXFileBadge() {
    return (
        <Tooltip title="GPX File">
            <Box
                sx={{
                    px: 0.5,
                    py: 0.25,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    color: '#666',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                    border: '1px solid',
                    borderColor: '#ddd',
                    flexShrink: 0
                }}
            >
                GPX
            </Box>
        </Tooltip>
    );
}

function ActionButtons({ row, onRouteSelect, isLoading, isMobile = false }) {
    if (isLoading) return null;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
            <Tooltip title="View Cues">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRouteSelect(row.id);
                    }}
                    sx={{
                        p: 0.5,
                        flexShrink: 0,
                        minWidth: 'auto',
                        minHeight: 'auto'
                    }}
                >
                    <VisibilityIcon sx={{
                        fontSize: '0.9rem'
                    }} />
                </IconButton>
            </Tooltip>
            {row.source !== 'gpx' && (
                <Tooltip title={row.source === 'strava' ? 'View on Strava' : 'View on RWGPS'}>
                    <IconButton
                        size="small"
                        component="a"
                        href={row.source === 'strava' ? `https://www.strava.com/routes/${row.id}` : `https://ridewithgps.com/routes/${row.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            p: 0.5,
                            minWidth: '32px',
                            minHeight: '32px'
                        }}
                    >
                        <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                </Tooltip>
            )}
            {row.isCached && (
                <Tooltip title="Available Offline">
                    <DownloadIcon sx={{
                        fontSize: '1rem',
                        color: 'success.main'
                    }} />
                </Tooltip>
            )}
        </Box>
    );
}

function ExpandedRowDetails({ row, chartSettings, onRouteSelect, onUpdateRoute, onRemoveFromCache, isOnline, isMobile, fileInputRef, onFileChange }) {
    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? 'Unknown' : date.toLocaleDateString();
    };

    return (
        <Box sx={{ py: isMobile ? 1 : 2, px: isMobile ? 1 : 2 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                            Distance: {Math.round(row.distance * chartSettings.distMultiplier)} {chartSettings.distAbbr}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Elevation: +{Math.round(row.elevation_gain * chartSettings.elevMultiplier)}{chartSettings.elevAbbr}{row.elevation_loss ? ` / -${Math.round(row.elevation_loss * chartSettings.elevMultiplier)}${chartSettings.elevAbbr}` : ''}
                        </Typography>
                        {row.source !== 'strava' && row.source !== 'gpx' && (
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    Surface: {row.surface || 'Unknown'} ({row.unpaved_pct || 0}% unpaved)
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Difficulty: {row.difficulty || 'Unknown'}
                                </Typography>
                            </>
                        )}
                        {row.isPublicRoute && (
                            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                                Public Route
                            </Typography>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {row.source !== 'strava' && row.source !== 'gpx' && (
                            <Typography variant="body2" color="text.secondary">
                                Country: {row.country_code || 'Unknown'}
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            Created: {formatDate(row.created_at)}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, borderTop: '1px solid rgba(0, 0, 0, 0.12)', pt: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRouteSelect(row.id);
                                }}
                                sx={{
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: '0.75rem',
                                    height: '32px'
                                }}
                            >
                                View Cues
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onUpdateRoute(row.id);
                                }}
                                disabled={row.source !== 'gpx' && !isOnline}
                                sx={{
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: '0.75rem',
                                    height: '32px'
                                }}
                            >
                                {row.isCached === true ? 'Update Route' : 'Save Offline'}
                            </Button>
                            {row.isCached && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRemoveFromCache(row.id);
                                    }}
                                    sx={{
                                        minWidth: 'auto',
                                        px: 1.5,
                                        py: 0.5,
                                        fontSize: '0.75rem',
                                        height: '32px'
                                    }}
                                >
                                    Clear
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {row.isCached && row.cachedAt && (
                                <Typography variant="body2" color="text.secondary">
                                    Saved: {(new Date(row.cachedAt)).toLocaleString()}
                                </Typography>
                            )}
                            {row.source !== 'gpx' && (
                                <Link
                                    href={row.source === 'strava' ? `https://www.strava.com/routes/${row.id}` : `https://ridewithgps.com/routes/${row.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: row.source === 'strava' ? 600 : 400,
                                        color: row.source === 'strava' ? '#FC5200' : 'primary.main',
                                        textDecoration: 'underline',
                                        '&:hover': {
                                            textDecoration: 'underline'
                                        }
                                    }}
                                >
                                    {row.source === 'strava' ? 'View on Strava' : 'View on RWGPS'}
                                </Link>
                            )}
                            {row.source === 'gpx' && (
                                <Box
                                    sx={{
                                        px: 0.5,
                                        py: 0.25,
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        color: '#666',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '4px',
                                        border: '1px solid',
                                        borderColor: '#ddd',
                                        display: 'inline-block',
                                        width: 'fit-content'
                                    }}
                                >
                                    GPX
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default RouteSelection;

